import React, { useState, useEffect } from 'react';
import { usePaginationV2 } from '@uhg-abyss/web/hooks/usePagination';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { V2ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { V2StepTracker } from '@uhg-abyss/web/ui/StepTracker';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useTinDrafts } from '@src/state/superUserDraft';
import { useUser } from '@src/state/user/hooks';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Loading } from '@src/common/Loading';
import { CLIA_PRACTICES } from '@src/state/tinRegistration/utils';
import { SubmitterInformation } from './SubmitterInformation';
import { W9Upload } from './W9Upload/W9Upload';
import { PracticeInformation } from './PracticeInformation';
import { NpiValidationModal } from './NpiValidationModal';
import { LocationInformation } from './LocationInformation';
import { Review } from './Review';
import { Attest } from './Attest';
import { MAX_SECONDARY_ADDRESSES } from './constants';

const PageContainer = styled('div', {
  padding: '30px 40px',
  maxWidth: '1400px',
  margin: '0 auto',
});

const PageHeading = styled('h1', {
  margin: 0,
});

const ContentPanel = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
});

const ButtonRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const CancelText = styled('span', {
  fontSize: '16px',
  fontWeight: 700,
  marginLeft: '20px',
  color: '$info1',
  cursor: 'pointer',
});

const SubmitTextGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  marginTop: '8px',
  marginBottom: '12px',
});

const ReferenceNumberText = styled('div', {
  color: '$primary1',
  fontWeight: 700,
  fontSize: '18px',
});

const STEPS = [
  'Submitter information',
  'W-9 information',
  'Practice information',
  'Location information',
  'Review',
  'Attest',
];
const SUBMITTER_INFO_STEP_INDEX = 0;
const W9_INFO_STEP_INDEX = 1;
const PRACTICE_INFO_STEP_INDEX = 2;
const LOCATION_INFO_STEP_INDEX = 3;
const REVIEW_STEP_INDEX = 4;
const ATTEST_STEP_INDEX = 5;

const EMPTY_STATIC_ADDRESS_ERRORS = {
  primaryServicing: false,
  primaryBilling: false,
  primaryRemittance: false,
};

const STATIC_LOCATION_SECTIONS = [
  {
    key: 'primaryServicing',
    addressModel: 'address.primaryServicing',
    proofModel: 'address.primaryServicingProof',
  },
  {
    key: 'primaryBilling',
    addressModel: 'address.primaryBilling',
    proofModel: 'address.primaryBillingProof',
  },
  {
    key: 'primaryRemittance',
    addressModel: 'address.primaryRemittance',
    proofModel: 'address.primaryRemittanceProof',
  },
];

const hasAnyValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') {
    return Object.values(value).some(hasAnyValue);
  }
  return Boolean(value);
};

const parseNpiList = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeFileList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value];
};

const getSingleFile = (value) => {
  const files = normalizeFileList(value);
  return files.length === 1 ? files[0] : null;
};

// ─── Component ────────────────────────────────────────────────────────────────

export const VerifyTin = () => {
  const router = useRouter();
  const abyssForm = useFormV2();
  const registration = abyssForm.getValues('searchRegistration');
  const tin = registration?.billingTin ?? '';
  const userInfo = useUser.state();
  const isSuperUser = storage.local.get(userInfo.uuid);

  const { saveAsDraft, setDocumentId, validateNPI, submitTin } =
    useTinRegistration.actions();
  const {
    trackingNumber,
    documentIds,
    isDocumentUploading,
    submitTinSaga,
    tinSubmission,
    isDraftDisabled,
  } = useTinRegistration.state();
  const isUploading = Object.values(isDocumentUploading || {}).some(Boolean);
  const { saveSuperUserDraft } = useTinDrafts.actions();
  const {
    pageIndex,
    nextPage,
    previousPage,
    goToPage,
    canNextPage,
    canPreviousPage,
  } = usePaginationV2({
    pages: STEPS.length,
  });

  const [saveDraftStatus, setSaveDraftStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [locationDoc360UploadErrors, setLocationDoc360UploadErrors] = useState(
    {}
  );
  const [locationAddressErrors, setLocationAddressErrors] = useState({
    static: EMPTY_STATIC_ADDRESS_ERRORS,
    secondary: new Set(),
  });
  const [locationAddrValidationCount, setLocationAddrValidationCount] =
    useState(0);
  const [stepAlert, setStepAlert] = useState(false);
  const [secondaryIds, setSecondaryIds] = useState([]);
  const [isNpiValidationModalOpen, setNpiValidationModalOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [submitModalStatus, setSubmitModalStatus] = useState(null); // null | 'success' | 'error'

  useEffect(() => {
    setSaveDraftStatus(null);
    setStepAlert(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pageIndex]);

  useEffect(() => {
    // Initialize secondary IDs from existing form data on mount.
    const existingSecondary = abyssForm.getValues('address.secondaryServicing');
    if (Array.isArray(existingSecondary)) {
      const ids = Object.keys(existingSecondary)
        .map((key) => Number(key))
        .filter(
          (id) =>
            Number.isInteger(id) && id >= 0 && id < MAX_SECONDARY_ADDRESSES
        )
        .sort((a, b) => a - b)
        .slice(0, MAX_SECONDARY_ADDRESSES);
      setSecondaryIds(ids);
    }
  }, []);

  useEffect(() => {
    return () => {
      // Clears this page's form data and associated document IDs from the
      // shared form context when navigating away, so stale data does not persist.

      // Using setValue instead of reset because reset only operates on currently
      // registered fields — by unmount time, children have already unregistered.
      // setValue writes directly to the form store regardless of fields registration.
      abyssForm.setValue('submitterInformation', undefined);
      abyssForm.setValue('w9form', undefined);
      abyssForm.setValue('practiceInformation', undefined);
      abyssForm.setValue('address', undefined);
      abyssForm.setValue('attestation', undefined);

      setDocumentId({ id: 'w9form.fileName', value: undefined });
      setDocumentId({ id: 'practiceInformation.cliaFile', value: undefined });
      setDocumentId({ id: 'address.primaryServicingProof', value: undefined });
      setDocumentId({ id: 'address.primaryBillingProof', value: undefined });
      setDocumentId({ id: 'address.primaryRemittanceProof', value: undefined });
      setDocumentId({ id: 'attestation.textSignature', value: undefined });
      setDocumentId({ id: 'attestation.drawSignature', value: undefined });
      Array.from({ length: MAX_SECONDARY_ADDRESSES }, (_, i) => i).forEach(
        (i) =>
          setDocumentId({
            id: `address.secondaryServicing[${i}].proof`,
            value: undefined,
          })
      );
    };
  }, []);

  useEffect(() => {
    if (!tin) {
      router.navigate('/');
    }
  }, []);

  useEffect(() => {
    const handleAfterPrint = () => {
      setIsPrinting(false);
    };

    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  useEffect(() => {
    if (!isPrinting) return;
    window.print();
  }, [isPrinting]);

  if (!tin) {
    return null;
  }

  const getRequestData = (saveType) => {
    const submitterInfo = abyssForm.getValues('submitterInformation') || {};
    const fullName = [submitterInfo.firstName, submitterInfo.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const isClaimSubmitted = submitterInfo.submittedClaim === 'Yes';
    const normalizedClaimDate = submitterInfo.claimDate
      ? dayjs(submitterInfo.claimDate).format('MM-DD-YYYY')
      : null;

    const w9Info = abyssForm.getValues('w9form') || {};
    const w9Files = normalizeFileList(w9Info.fileName);
    const hasValidW9FileCount = w9Files.length === 1;
    const w9Doc360Id = documentIds?.['w9form.fileName'] || null;

    const practiceInfo = abyssForm.getValues('practiceInformation') || {};
    const practiceType = practiceInfo.practiceTypeSelection || '';
    const isClia = CLIA_PRACTICES.includes(practiceType);
    const isAsc = practiceType === 'Ambulatory Surgery Center';
    const cliaValue = abyssForm.getValues('practiceInformation.cliaFile');
    const cliaFiles = normalizeFileList(cliaValue);
    const hasValidCliaFileCount = cliaFiles.length === 1;
    const cliaDoc360Id = documentIds?.['practiceInformation.cliaFile'] || null;
    const practiceLicenses = Array.isArray(practiceInfo.medLicense)
      ? practiceInfo.medLicense
      : [];
    const licenses = practiceLicenses
      .filter((med) => med && (med.licenseType || med.licenseNumber))
      .map((med) => ({
        licenseType: med.licenseType || '',
        licenseNumber: med.licenseNumber || '',
        state: med.state || '',
      }));
    const npi = parseNpiList(practiceInfo.npiInformation);
    const practiceInformation = {
      registrationType: practiceInfo.registrationTypeSelection || null,
      practiceType: practiceType || null,
      cliaId: isClia ? practiceInfo.cliaIdentification || '' : '',
      medicareId: isAsc ? practiceInfo.medicareId || '' : '',
      fileName:
        isClia && hasValidCliaFileCount && cliaDoc360Id
          ? cliaFiles[0].name
          : null,
      doc360IdCms:
        isClia && hasValidCliaFileCount && cliaDoc360Id ? cliaDoc360Id : null,
      isRevoked: practiceInfo.restrictionSelection === 'Yes',
      additionalDetails:
        practiceInfo.restrictionSelection === 'Yes'
          ? practiceInfo.restrictionInput || null
          : null,
      npi,
      isNpiValid: practiceInfo.isNpiValid,
      licenses,
    };

    const addrValues = abyssForm.getValues('address') || {};
    const getUploadedFileInfo = (proofDocModel, proofFiles) => {
      const normalizedFiles = normalizeFileList(proofFiles);
      const hasValidFileCount = normalizedFiles.length === 1;
      const addrDoc360Id = documentIds?.[proofDocModel] || null;
      return {
        doc360Id: hasValidFileCount && addrDoc360Id ? addrDoc360Id : null,
        fileName:
          hasValidFileCount && addrDoc360Id ? normalizedFiles[0].name : null,
      };
    };
    const buildDraftAddressEntry = ({ fields, type, upload }) => {
      const { doc360Id, fileName } = upload;

      return {
        streetLine1: fields?.streetLine1 || null,
        streetLine2: fields?.streetLine2 || null,
        city: fields?.city || null,
        state: fields?.state || null,
        zipCode: fields?.zipCode || null,
        type,
        doc360Id,
        fileName,
      };
    };
    const address = [
      {
        fields: addrValues.primaryServicing,
        upload: getUploadedFileInfo(
          'address.primaryServicingProof',
          addrValues.primaryServicingProof
        ),
        type: 'PrimaryService',
      },
      {
        fields: addrValues.primaryBilling,
        upload: getUploadedFileInfo(
          'address.primaryBillingProof',
          addrValues.primaryBillingProof
        ),
        type: 'PrimaryBilling',
      },
      {
        fields: addrValues.primaryRemittance,
        upload: getUploadedFileInfo(
          'address.primaryRemittanceProof',
          addrValues.primaryRemittanceProof
        ),
        type: 'PrimaryRemit',
      },
      ...secondaryIds.map((id) => {
        const sec = addrValues.secondaryServicing?.[id];
        return {
          fields: sec,
          upload: getUploadedFileInfo(
            `address.secondaryServicing[${id}].proof`,
            sec?.proof
          ),
          type: `SecondaryService${id + 1}`,
        };
      }),
    ].map(buildDraftAddressEntry);

    const attestInfo = abyssForm.getValues('attestation') || {};

    return {
      billingTin: tin,
      requestId: registration?.requestId || null,
      trackingNumber: trackingNumber ?? '',
      w9Details: {
        name: w9Info.name || null,
        businessName: w9Info.businessName || null,
        federalTaxClassification: w9Info.federalTaxClassification || null,
        fileName: hasValidW9FileCount && w9Doc360Id ? w9Files[0].name : null,
        doc360Id: hasValidW9FileCount && w9Doc360Id ? w9Doc360Id : null,
      },
      practiceInformation:
        practiceInformation.registrationType ||
        practiceInformation.practiceType ||
        (practiceInformation.npi && practiceInformation.npi[0]) ||
        practiceInformation.licenses.length > 0
          ? practiceInformation
          : null,
      address,
      attestationDetails: {
        name: saveType === 'submit' ? attestInfo.submitterName : fullName,
        phoneNumber: submitterInfo.phoneNumber,
        jobTitle: submitterInfo.jobTitle,
        email: submitterInfo.emailAddress,
        communicationPreference: submitterInfo.preferredModeOfContact,
        claimDate: isClaimSubmitted ? normalizedClaimDate : null,
        isClaimSubmitted,
        doc360Id: (() => {
          if (saveType === 'draft') return null;
          if (saveType === 'submit') {
            const signatureTab = attestInfo.signatureTab;
            return signatureTab === 'Draw'
              ? documentIds?.['attestation.drawSignature'] || null
              : signatureTab === 'Text'
              ? documentIds?.['attestation.textSignature'] || null
              : null;
          }
          return null;
        })(),
      },
    };
  };

  const getLocationProofModels = () => {
    return [
      ...STATIC_LOCATION_SECTIONS.map((section) => section.proofModel),
      ...secondaryIds.map((id) => `address.secondaryServicing[${id}].proof`),
    ];
  };

  const isProofModelReadyForSubmit = (model) => {
    const file = getSingleFile(abyssForm.getValues(model));
    const hasFile = !!file;
    const hasDoc360Id = !!documentIds?.[model];
    const hasUploadError = !!locationDoc360UploadErrors?.[model];
    return hasFile && hasDoc360Id && !hasUploadError;
  };

  const handleSaveAsDraft = () => {
    setSaveDraftStatus('loading');

    const body = getRequestData('draft');

    if (!isSuperUser) {
      saveAsDraft(
        body,
        () => setSaveDraftStatus('success'),
        () => setSaveDraftStatus('error')
      );
    } else {
      saveSuperUserDraft(
        { ...body, optumId: registration?.optumId },
        () => setSaveDraftStatus('success'),
        () => setSaveDraftStatus('error')
      );
    }
  };

  const reValidateAddresses = async ({
    fromBlur = false,
    scope = null,
    secondaryIdsOverride = null,
  } = {}) => {
    if (fromBlur && locationAddrValidationCount === 0) {
      return;
    }

    const result = await getLocationValidationState({
      runWhenNoUpdate: false,
      scope,
      secondaryIdsOverride,
    });

    if (scope) {
      if (result.skipped) {
        return;
      }

      setLocationAddressErrors((prev) => {
        const nextStatic = {
          ...(prev?.static || EMPTY_STATIC_ADDRESS_ERRORS),
        };
        const nextSecondary = new Set(prev?.secondary || []);

        if (scope.type === 'static' && scope.key) {
          nextStatic[scope.key] = !!result.staticErrors?.[scope.key];
        }

        if (scope.type === 'secondary' && Number.isInteger(scope.id)) {
          if (result.secondaryErrors?.has(scope.id)) {
            nextSecondary.add(scope.id);
          } else {
            nextSecondary.delete(scope.id);
          }
        }

        return {
          static: nextStatic,
          secondary: nextSecondary,
        };
      });
      return;
    }

    if (result.skipped) {
      if (!fromBlur) {
        setStepAlert('location-info-no-update');
      }
      return;
    }

    if (!result.isValid) {
      if (!fromBlur) {
        setStepAlert(true);
      }
      setLocationAddressErrors({
        static: result.staticErrors,
        secondary: result.secondaryErrors,
      });
      if (!fromBlur) {
        setLocationAddrValidationCount((c) => c + 1);
      }
      return;
    }

    setStepAlert(false);
    setLocationAddressErrors({
      static: EMPTY_STATIC_ADDRESS_ERRORS,
      secondary: new Set(),
    });
    return;
  };

  const getLocationValidationState = async ({
    runWhenNoUpdate,
    scope = null,
    secondaryIdsOverride = null,
  }) => {
    const validateModel = (model) =>
      new Promise((resolve) =>
        abyssForm.validate(
          model,
          () => resolve(true),
          () => resolve(false)
        )
      );

    const staticSectionScope =
      scope?.type === 'static' &&
      STATIC_LOCATION_SECTIONS.find((section) => section.key === scope.key);
    const secondaryScopeId =
      scope?.type === 'secondary' && Number.isInteger(scope.id)
        ? scope.id
        : null;
    const allSecondaryIds = Array.isArray(secondaryIdsOverride)
      ? secondaryIdsOverride
      : secondaryIds;

    const staticSectionsToValidate = staticSectionScope
      ? [staticSectionScope]
      : STATIC_LOCATION_SECTIONS;
    const secondaryIdsToValidate = Number.isInteger(secondaryScopeId)
      ? [secondaryScopeId]
      : allSecondaryIds;

    const addressValues = abyssForm.getValues('address') || {};
    const hasPrimaryUpdate = staticSectionScope
      ? [
          abyssForm.getValues(staticSectionScope.addressModel),
          abyssForm.getValues(staticSectionScope.proofModel),
        ].some(hasAnyValue)
      : [
          addressValues.primaryServicing,
          addressValues.primaryServicingProof,
          addressValues.primaryBilling,
          addressValues.primaryBillingProof,
          addressValues.primaryRemittance,
          addressValues.primaryRemittanceProof,
        ].some(hasAnyValue);

    const hasSecondaryUpdate = Number.isInteger(secondaryScopeId)
      ? allSecondaryIds.includes(secondaryScopeId)
      : allSecondaryIds.length > 0;

    const hasAnyLocationUpdate = hasPrimaryUpdate || hasSecondaryUpdate;

    if (!scope && !hasAnyLocationUpdate && !runWhenNoUpdate) {
      return { skipped: true, hasAnyLocationUpdate };
    }

    const staticAddressValidationResults = await Promise.all(
      staticSectionsToValidate.map(async (section) => {
        const [addressValid, proofValid] = await Promise.all([
          validateModel(section.addressModel),
          validateModel(section.proofModel),
        ]);

        return {
          key: section.key,
          proofModel: section.proofModel,
          hasError: !(addressValid && proofValid),
        };
      })
    );

    const staticSectionErrors = { ...EMPTY_STATIC_ADDRESS_ERRORS };
    staticAddressValidationResults.forEach(({ key, hasError }) => {
      staticSectionErrors[key] = hasError;
    });
    const staticValidationsOk = staticAddressValidationResults.every(
      ({ hasError }) => !hasError
    );

    const secondaryValidationResults = await Promise.all(
      secondaryIdsToValidate.map(async (id) => ({
        id,
        isValid: await validateModel(`address.secondaryServicing[${id}]`),
      }))
    );
    const invalidSecondaryIds = new Set(
      secondaryValidationResults
        .filter(({ isValid }) => !isValid)
        .map(({ id }) => id)
    );

    const locationProofModels = [
      ...staticSectionsToValidate.map((section) => section.proofModel),
      ...secondaryIdsToValidate.map(
        (id) => `address.secondaryServicing[${id}].proof`
      ),
    ];

    const failedModels = new Set();
    locationProofModels.forEach((model) => {
      const value = abyssForm.getValues(model);
      const file = Array.isArray(value) ? value[0] : value;
      const hasFile = !!file;
      const hasDoc360Id = !!documentIds?.[model];
      const hasDoc360Error = !!locationDoc360UploadErrors?.[model];
      const hasMissingRequiredProof = !hasFile && !hasDoc360Id;

      // Required proof missing, API error, or file exists but no doc360Id.
      if (
        hasMissingRequiredProof ||
        hasDoc360Error ||
        (hasFile && !hasDoc360Id)
      ) {
        failedModels.add(model);
      }
    });

    const fileSectionErrors = { ...EMPTY_STATIC_ADDRESS_ERRORS };
    staticSectionsToValidate.forEach((section) => {
      fileSectionErrors[section.key] = failedModels.has(section.proofModel);
    });

    const failedSecondaryIds = new Set();
    secondaryIdsToValidate.forEach((id) => {
      if (failedModels.has(`address.secondaryServicing[${id}].proof`)) {
        failedSecondaryIds.add(id);
      }
    });

    const staticErrors = {
      primaryServicing:
        staticSectionErrors.primaryServicing ||
        fileSectionErrors.primaryServicing,
      primaryBilling:
        staticSectionErrors.primaryBilling || fileSectionErrors.primaryBilling,
      primaryRemittance:
        staticSectionErrors.primaryRemittance ||
        fileSectionErrors.primaryRemittance,
    };

    const secondaryErrors = new Set([
      ...invalidSecondaryIds,
      ...failedSecondaryIds,
    ]);

    let isValid;
    if (staticSectionScope) {
      const sectionKey = staticSectionScope.key;
      isValid = !staticErrors[sectionKey];
    } else if (Number.isInteger(secondaryScopeId)) {
      isValid =
        !invalidSecondaryIds.has(secondaryScopeId) &&
        !failedSecondaryIds.has(secondaryScopeId);
    } else {
      isValid =
        staticValidationsOk &&
        invalidSecondaryIds.size === 0 &&
        failedModels.size === 0;
    }

    return {
      skipped: false,
      hasAnyLocationUpdate,
      isValid,
      staticErrors,
      secondaryErrors,
    };
  };

  const goNext = async () => {
    if (pageIndex === SUBMITTER_INFO_STEP_INDEX) {
      abyssForm.validate(
        'submitterInformation',
        () => nextPage(),
        () => {}
      );
      return;
    } else if (pageIndex === W9_INFO_STEP_INDEX) {
      abyssForm.validate(
        'w9form',
        () => {
          const w9File = getSingleFile(abyssForm.getValues('w9form.fileName'));
          const hasW9Doc360Id = !!documentIds?.['w9form.fileName'];

          if (!w9File || !hasW9Doc360Id) {
            setStepAlert(true);
            return;
          }

          setStepAlert(false);
          nextPage();
        },
        () => {}
      );
      return;
    } else if (pageIndex === PRACTICE_INFO_STEP_INDEX) {
      abyssForm.validate(
        'practiceInformation',
        (values) => {
          const npiValues = parseNpiList(values.npiInformation);

          if (npiValues.length === 0) {
            abyssForm.setValue('practiceInformation.isNpiValid', true);
            nextPage();
            return;
          }

          validateNPI(
            {
              isProvider: values.registrationTypeSelection === 'Individual',
              npi: npiValues[0],
              trackingNumber,
            },
            () => {
              abyssForm.setValue('practiceInformation.isNpiValid', true);
              setNpiValidationModalOpen(false);
              nextPage();
            },
            () => {
              setNpiValidationModalOpen(true);
            }
          );
        },
        () => {}
      );
      return;
    } else if (pageIndex === LOCATION_INFO_STEP_INDEX) {
      const result = await getLocationValidationState({
        runWhenNoUpdate: true,
      });

      if (!result.isValid) {
        if (!result.hasAnyLocationUpdate) {
          setStepAlert('location-info-no-update');
        } else {
          setStepAlert(true);
        }
        setLocationAddressErrors({
          static: result.staticErrors,
          secondary: result.secondaryErrors,
        });
        setLocationAddrValidationCount((c) => c + 1);
        return;
      }

      setStepAlert(false);
      setLocationAddressErrors({
        static: EMPTY_STATIC_ADDRESS_ERRORS,
        secondary: new Set(),
      });
      nextPage();
      return;
    } else nextPage();
  };

  const handlePrintClick = () => {
    setIsPrinting(true);
  };

  const renderCurrentStep = () => {
    return (
      <>
        {pageIndex === SUBMITTER_INFO_STEP_INDEX && <SubmitterInformation />}
        <div
          style={{
            display: pageIndex === W9_INFO_STEP_INDEX ? 'contents' : 'none',
          }}
        >
          <W9Upload />
        </div>
        <div
          style={{
            display:
              pageIndex === PRACTICE_INFO_STEP_INDEX ? 'contents' : 'none',
          }}
        >
          <PracticeInformation />
        </div>
        <div
          style={{
            display:
              pageIndex === LOCATION_INFO_STEP_INDEX ? 'contents' : 'none',
          }}
        >
          <LocationInformation
            secondaryIds={secondaryIds}
            setSecondaryIds={setSecondaryIds}
            doc360UploadErrors={locationDoc360UploadErrors}
            setDoc360UploadErrors={setLocationDoc360UploadErrors}
            locationAddressErrors={locationAddressErrors}
            addrValidationCount={locationAddrValidationCount}
            reValidateAddresses={reValidateAddresses}
          />
        </div>
        {pageIndex === REVIEW_STEP_INDEX && (
          <Review
            onEdit={goToPage}
            isPrinting={isPrinting}
            onPrintClick={handlePrintClick}
          />
        )}
        <div
          style={
            pageIndex !== ATTEST_STEP_INDEX
              ? {
                  position: 'absolute',
                  left: '-100vw',
                  visibility: 'hidden',
                  pointerEvents: 'none',
                }
              : {}
          }
        >
          <Attest />
        </div>
      </>
    );
  };

  const navigateHome = () => {
    router.navigate('/');
  };

  const handleContinueAfterNpiWarning = () => {
    abyssForm.setValue('practiceInformation.isNpiValid', false);
    setNpiValidationModalOpen(false);
    nextPage();
  };

  const handleSubmit = () => {
    abyssForm.validate(
      'attestation',
      async () => {
        // Attest validations passed — also check for signature doc360Id presence
        const signatureTab = abyssForm.getValues('attestation.signatureTab');
        const hasTextDoc360 = !!documentIds?.['attestation.textSignature'];
        const hasDrawDoc360 = !!documentIds?.['attestation.drawSignature'];
        const signatureDoc360Missing =
          (signatureTab === 'Text' && !hasTextDoc360) ||
          (signatureTab === 'Draw' && !hasDrawDoc360);

        if (signatureDoc360Missing) {
          if (
            signatureTab === 'Text' &&
            abyssForm.getValues('attestation.textSignature')
          ) {
            // Error is shown via ElectronicSignature's 'Please save' div
          } else if (signatureTab === 'Draw') {
            abyssForm.trigger('attestation.drawSignature');
          }
          return;
        }

        const w9File = getSingleFile(abyssForm.getValues('w9form.fileName'));
        const hasW9Doc360Id = !!documentIds?.['w9form.fileName'];
        if (!w9File || !hasW9Doc360Id) {
          setStepAlert(true);
          goToPage(W9_INFO_STEP_INDEX);
          return;
        }

        const locationResult = await getLocationValidationState({
          runWhenNoUpdate: true,
        });

        if (!locationResult.isValid) {
          if (!locationResult.hasAnyLocationUpdate) {
            setStepAlert('location-info-no-update');
          } else {
            setStepAlert(true);
          }
          setLocationAddressErrors({
            static: locationResult.staticErrors,
            secondary: locationResult.secondaryErrors,
          });
          setLocationAddrValidationCount((c) => c + 1);
          goToPage(LOCATION_INFO_STEP_INDEX);
          return;
        }

        const missingLocationUploads = getLocationProofModels().some(
          (model) => !isProofModelReadyForSubmit(model)
        );

        if (missingLocationUploads) {
          setStepAlert(true);
          goToPage(LOCATION_INFO_STEP_INDEX);
          return;
        }

        setStepAlert(false);

        // Signature doc360Id present — proceed with submit
        const body = getRequestData('submit');

        submitTin(
          {
            ...body,
            billingTin: registration.billingTin,
            requestId: registration.requestId || null,
            trackingNumber,
          },
          (response) => {
            setSubmitModalStatus('success');
          },
          (error) => {
            setSubmitModalStatus('error');
          }
        );
      },
      () => {
        // Validation failed - form shows errors, no action needed
      }
    );
  };

  return (
    <Loading
      isLoading={
        saveDraftStatus === 'loading' || isUploading || submitTinSaga.isLoading
      }
    >
      <PageContainer>
        <Layout.Group alignLayout="between" css={{ marginBottom: '20px' }}>
          <PageHeading>Verify TIN: {tin}</PageHeading>

          {!isPrinting && (
            <V2Button
              variant="outline"
              size="$sm"
              icon={{ icon: 'arrow_back', position: 'leading' }}
              onClick={navigateHome}
            >
              Return to dashboard
            </V2Button>
          )}
        </Layout.Group>

        <ContentPanel>
          <NpiValidationModal
            isOpen={isNpiValidationModalOpen}
            onConfirm={handleContinueAfterNpiWarning}
            onCancel={() => setNpiValidationModalOpen(false)}
          />

          {!isPrinting && (
            <>
              <V2StepTracker
                steps={STEPS}
                currentStep={pageIndex + 1}
                hideStepNumbers
                hideStepStatus
              />

              {stepAlert && (
                <V2Alert
                  status="error"
                  title={
                    stepAlert === 'location-info-no-update'
                      ? 'Please update your location information before continuing.'
                      : 'Please review and correct any errors or missing required information to continue.'
                  }
                  dismissible={false}
                  inline
                />
              )}

              {saveDraftStatus === 'success' && (
                <V2Alert
                  status="success"
                  title="Draft saved successfully."
                  dismissible
                  onClose={() => setSaveDraftStatus(null)}
                  inline
                />
              )}
              {saveDraftStatus === 'error' && (
                <V2Alert
                  status="error"
                  title="Failed to save draft. Please try again."
                  dismissible
                  onClose={() => setSaveDraftStatus(null)}
                  inline
                />
              )}
            </>
          )}

          {renderCurrentStep()}

          {!isPrinting && (
            <ButtonRow>
              {canPreviousPage ? (
                <V2Button variant="outline" onClick={previousPage}>
                  Back
                </V2Button>
              ) : (
                <CancelText onClick={navigateHome}>Cancel</CancelText>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                {canNextPage && (
                  <V2Button
                    variant="outline"
                    onClick={handleSaveAsDraft}
                    isDisabled={isDraftDisabled}
                  >
                    Save as draft
                  </V2Button>
                )}
                {canNextPage ? (
                  <V2Button onClick={goNext}>Continue</V2Button>
                ) : (
                  <V2Button
                    variant={!!isSuperUser ? 'outline' : 'filled'}
                    onClick={handleSubmit}
                    isDisabled={!!isSuperUser}
                  >
                    Submit
                  </V2Button>
                )}
              </div>
            </ButtonRow>
          )}
        </ContentPanel>

        <V2ModalDialog
          isOpen={submitModalStatus !== null}
          hideClose
          closeOnClickOutside={false}
          size="720px"
          css={{
            'abyss-modal-base-header': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              width: '100%',
              textAlign: 'center',
            },
            'abyss-modal-base-body': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              textAlign: 'center',
            },
          }}
          header={
            <>
              <IconBrand
                icon={submitModalStatus === 'success' ? 'checkmark' : 'alert'}
                size="70px"
              />
              {submitModalStatus === 'success'
                ? 'Thank you for your submission'
                : 'Submission unsuccessful'}
            </>
          }
        >
          {submitModalStatus === 'success' ? (
            <SubmitTextGroup>
              <ReferenceNumberText>
                Your reference number is {tinSubmission?.trackingNumber}
              </ReferenceNumberText>
              You will receive an email once your registration has been
              processed.
            </SubmitTextGroup>
          ) : (
            <SubmitTextGroup>
              There was an error submitting your application. Please try again.
            </SubmitTextGroup>
          )}
          <V2Button
            icon={
              submitModalStatus === 'success'
                ? { icon: 'arrow_back', position: 'leading' }
                : undefined
            }
            onClick={() =>
              submitModalStatus === 'success'
                ? navigateHome()
                : setSubmitModalStatus(null)
            }
            variant={submitModalStatus === 'success' ? 'filled' : 'outline'}
          >
            {submitModalStatus === 'success' ? 'Return to dashboard' : 'Close'}
          </V2Button>
        </V2ModalDialog>
      </PageContainer>
    </Loading>
  );
};
